import React, { FunctionComponent, useEffect, useState } from 'react'
import Axios from 'axios'
import { useDebounce } from 'use-debounce'
import { getCulture, parseHtml, sanitizeUrl } from './helpers'
import { t } from './translate'
import { toggleMobileNav } from '../hooks/mobile-nav.hook'

declare global {
  interface Window {
    openSearch: () => any
    lightGallery: (e: Element, options?: any) => { refresh: () => any }
  }
}

export const Search: FunctionComponent = () => {
  const [keyword, setKeyword] = useState('')
  const [visible, setVisible] = useState(false)
  const [spellingSuggestion, setSpellingSuggestion] = useState('')
  const [results, setResults] = useState<
    {
      id: string
      image: string
      url: string
      title: string
      body: string
      highlight?: {
        body: string
        sections: string[]
      }
    }[]
  >([])
  const [resultCount, setResultCount] = useState(0)
  const [debouncedSearchTerm] = useDebounce(keyword, 400)

  useEffect(() => {
    const existingOpenSearch = window.openSearch
    if (existingOpenSearch) {
      window.openSearch = () => {
        existingOpenSearch()
        setVisible(true)
      }
    } else {
      window.openSearch = () => setVisible(true)
    }
  }, [])

  useEffect(() => {
    if (debouncedSearchTerm.length < 3) {
      return
    }
    Axios.get(
      'https://search-api.swiftype.com/api/v1/public/engines/search.json',
      {
        params: {
          q: debouncedSearchTerm,
          engine_key: '1kX_gCwkSezbYzMBCxmf',
          spelling: 'strict',
          page: 1,
          per_page: 20,
          filters: {
            page: {
              culture: getCulture(),
            },
          },
        },
      },
    ).then((res) => {
      setResults(res.data.records.page)
      setResultCount(res.data.record_count)
      setSpellingSuggestion(
        res.data?.info?.page?.spelling_suggestion?.text || '',
      )
      if (document.querySelector('.Navigation--mobile.Navigation--open')) {
        toggleMobileNav()
      }
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (!visible) {
      setResults([])
      setKeyword('')
      setResultCount(0)
    }
  }, [visible])

  if (!visible) {
    return null
  }

  return (
    <>
      <div
        className="Search__overlay"
        onClick={() => {
          setVisible(false)
        }}
      ></div>
      <div className="Search__bar">
        <svg>
          <use xlinkHref="#search"></use>
        </svg>
        <input
          value={keyword}
          autoFocus
          onChange={(e) => {
            setKeyword(e.target.value)
          }}
          type="text"
          className="Serch__input"
          spellCheck="true"
        ></input>
        <svg
          onClick={() => {
            setVisible(false)
          }}
        >
          <use xlinkHref="#x-symbol"></use>
        </svg>
      </div>
      {keyword.length != 0 && (
        <div className="Search__suggestions custom-scrollbar">
          {resultCount == 0 ? (
            <h3>{t('search.suche_kein_ergebnis')}</h3>
          ) : (
            <h3>
              {resultCount} {t('search.results')}
            </h3>
          )}
          <div className="Search__suggestions__keyword">
            <svg>
              <use xlinkHref="#search-duo" />
            </svg>
            {resultCount == 0 &&
              keyword.length != 0 &&
              spellingSuggestion.length != 0 ? (
              <span>
                {t('search.meinten_sie')} &lsquo;
                <a
                  onClick={() => {
                    setKeyword(spellingSuggestion)
                  }}>
                  <strong className='Search__suggestions__keyword__result'>{spellingSuggestion}</strong>
                </a>
                &rsquo;
              </span>
            ) : resultCount == 0 &&
              keyword.length != 0 &&
              spellingSuggestion.length == 0 ? (
              <span>{t('search.suche_kein_ergebnis')}</span>
            ) : (
              <span>
                {t('search.suche')} &lsquo;<strong>{keyword}</strong>&rsquo;
              </span>
            )}
          </div>
          {results.map((result) => (
            <a href={sanitizeUrl(result.url)} className="Search__result" key={result.id}>
              {result.image && (
                <div className="Search__result__image">
                  <img src={result.image} alt="" />
                </div>
              )}
              <div className="Search__result__content">
                <div className="Search__result__headline">
                  {parseHtml(result.title)}
                </div>
                <div className="Search__result__text">
                  {parseHtml(result.body)}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  )
}
export default Search;
